<template>
  <div
    :style="[
      section.styles.cssVariables.value,
      `--section-primary-color: ${section.data?.value?.primaryColor}`,
      `--el-color-primary: ${section.data?.value?.primaryColor}`,
      `--color-primary-500: ${colorPalette['builder-primary-500']}`,
      `--color-primary-500-15: ${colorPalette['builder-primary-500/15']}`,
      `--color-primary-600: ${colorPalette['builder-primary-600']}`,
      `--color-primary-700: ${colorPalette['builder-primary-700']}`,
      props.embedParams?.backgroundColor ? `background-color: ${props.embedParams?.backgroundColor}` : '',
      props.embedParams?.verticalSpacing ? `padding-top: ${props.embedParams?.verticalSpacing}; padding-bottom: ${props.embedParams?.verticalSpacing}` : '',
    ]"
    class="text-(fg-default)"
    :class="[
      !props.embedParams?.verticalSpacing ? section.styles.spacerTop.value : '',
      !props.embedParams?.verticalSpacing ? section.styles.spacerBottom.value : '',
      !props.embedParams?.backgroundColor ? `bg-(bg-default)` : '',
    ]"
  >
    <div
      class="max-w-[35rem] mx-auto border border-(border-muted) rounded-lg bg-white text-black"
      :style="props.embedParams?.backgroundColor ? `border: 1px solid ${colorPalette['builder-gray-200']};` : ''"
    >
      <div
        v-if="isSubmitted || useBuilderStore?.().formBasicShowSubmitted"
        class="flex flex-col justify-center items-center py-44 px-8"
      >
        <Lottie
          :animation-data="checkLottie"
          :height="150"
          :width="150"
          :loop="false"
          class="lottie-check"
        />
        <template v-if="props.guest && props.guest?.status?.confirmation > 0">
          <h3 class="mb-1 text-center">
            {{ section.$t('poly.registrationModified') }}
          </h3>
        </template>
        <template v-else>
          <h3 class="mb-1 text-center">
            {{ ticketsTotalQuantity > 1 ? section.$t('poly.bulkPurchaseSubmittedTitle') : (props.data?.successBox?.[lang]?.title || section.$t('poly.attendanceConfirmed')) }}
          </h3>
          <p class="m-0 text-[#48484A] text-center">
            {{ ticketsTotalQuantity > 1 ? section.$t('poly.bulkPurchaseSubmittedDescription') : (props.data?.successBox?.[lang]?.description || section.$t('poly.registrationConfirmed.thankYou')) }}
          </p>
        </template>
      </div>
      <template v-else>
        <div
          v-if="!isBeforeEndDate"
          class="py-20"
        >
          <EmptyState
            :title="section.$t('poly.eventEnded.title')"
            :description="section.$t('poly.eventEnded.description')"
            class="h-full"
          />
        </div>
        <div
          v-else-if="props.stats?.guests >= props.event?.planMaxCapacity && !props.guest?._id"
          class="py-20"
        >
          <EmptyState
            :title="section.$t('poly.maximumCapacityReached.title')"
            :description="section.$t('poly.maximumCapacityReached.description')"
            class="h-full"
          />
        </div>
        <div v-else-if="!isRegistrationLimitValid">
          <EmptyState
            :title="section.$t('poly.registrationsNoLongerAvailable.title')"
            :description="section.$t('poly.registrationsNoLongerAvailable.description')"
            class="h-full"
          />
        </div>
        <div
          v-else
          class="py-6 xs:py-8 px-5 xs:px-12"
        >
          <transition
            name="custom-classes"
            :enter-from-class="isTicketingEnabled && isSelectingTicketTypes ? '-translate-x-4 opacity-0' : 'translate-x-4 opacity-0'"
            :leave-active-class="isTicketingEnabled && isSelectingTicketTypes ? 'translate-x-4 opacity-0' : '-translate-x-4 opacity-0'"
            class="transition-all duration-300"
            :duration="300"
            mode="out-in"
          >
            <PageFormBasicTickets
              v-if="isTicketingEnabled && isSelectingTicketTypes"
              v-model:selected-ticket-types="selectedTicketTypes"
              :event="props.event"
              :ticketing-types="ticketingTypesFiltered"
              :lang="lang"
              :form="form"
              :section="section"
              @submit="() => {
                if (hasDifferentTicketTypes(selectedTicketTypesBeforeEdit, selectedTicketTypes)){
                  resetForm();
                }
                if (ticketsTotalQuantity === 1) { // single ticket
                  form.properties.ticketType = selectedTicketTypes?.[0]?.id;
                }
                isSelectingTicketTypes = false;
              }"
            />
            <Form
              v-else
              :model="form"
              :rules="rules"
              @submit="onSubmit"
            >
              <Badge
                v-if="props.guest?.status?.confirmation > 0 || props.guest?.extras?.payment?.isPaid"
                :text="props.guest?.extras?.payment?.isPaid && props.guest?.status?.confirmation > 0
                  ? section.$t('poly.attendanceConfirmedAndPaid')
                  : props.guest?.extras?.payment?.isPaid ? section.$t('poly.guest.paid') : section.$t('poly.attendanceConfirmed')"
                type="success"
                class="mb-4"
              />

              <Badge
                v-if="props.guest?.status?.confirmation === -1"
                :text="section.$t('poly.attendanceRejected')"
                type="critical"
                class="mb-4"
              />

              <InputBlock
                v-if="isTicketingEnabled && ticketsTotalQuantity === 1"
                prop="singleSelectedTicketType"
                :label="section.$t('poly.ticket')"
              >
                <PageFormBasicTicketsTicket
                  :selected-ticket-types="selectedTicketTypesPopulated"
                  :ticketing-types="ticketingTypesFiltered"
                  :ticketing-type="selectedTicketTypesPopulated[0]"
                  :lang="lang"
                  :form="form"
                  :guest="props.guest"
                  :event="props.event"
                  :section="section"
                  is-single-summary
                  class="w-full"
                  @edit="(selectedTicketTypes) => {
                    selectedTicketTypesBeforeEdit = selectedTicketTypes;
                    isSelectingTicketTypes = true;
                  }"
                />

                <PageFormBasicPromoCode
                  v-if="isPaymentRequired || form.extras?.payment?.usedPromoCodes?.length"
                  :section="section"
                  :form="form"
                  :event="props.event"
                  :tickets-total-quantity="ticketsTotalQuantity"
                  class="mt-2"
                  @set-promo-codes="(promoCodes) => {
                    _set(form, 'extras.payment.usedPromoCodes', promoCodes);
                  }"
                />
              </InputBlock>

              <InputBlock
                v-if="isTicketingEnabled && ticketsTotalQuantity > 1"
                prop="ticketsSummary"
              >
                <PageFormBasicTicketsSummary
                  :ticket-types="selectedTicketTypesPopulated"
                  :event="props.event"
                  :lang="lang"
                  :total-price="ticketsTotalPrice"
                  :section="section"
                  @edit="(selectedTicketTypes) => {
                    selectedTicketTypesBeforeEdit = selectedTicketTypes;
                    isSelectingTicketTypes = true;
                  }"
                >
                  <PageFormBasicPromoCode
                    v-if="isPaymentRequired || form.extras?.payment?.usedPromoCodes?.length"
                    :section="section"
                    :form="form"
                    :event="props.event"
                    :tickets-total-quantity="ticketsTotalQuantity"
                    @set-promo-codes="(promoCodes) => {
                      _set(form, 'extras.payment.usedPromoCodes', promoCodes);
                    }"
                  />
                </PageFormBasicTicketsSummary>
              </InputBlock>

              <InputBlock
                v-for="property in baseProperties"
                :key="property.id"
                :label="property[lang]?.name"
                :prop="`properties.${property.id}`"
                show-asterisk
              >
                <TextInput
                  v-if="property.type === 'text'"
                  v-model="form.properties[property.id]"
                  :type="property.id === 'email' ? 'email' : 'text'"
                  :disabled="!!props.guest?.properties?.[property.id] && !property.canChange"
                  :autocomplete="property.id === 'email' ? 'email'
                    : property.id === 'firstName' ? 'given-name'
                      : property.id === 'lastName' ? 'family-name' : 'off'"
                />
              </InputBlock>

              <template v-if="!props.event?.general?.ticketing?.isBulkPurchaseEnabled || ticketsTotalQuantity < 2">
                <template v-if="isMounted || useRoute()?.path?.includes('/template/')">
                  <!-- fixing select SSR bug -->
                  <PageFormBasicInputBlock
                    v-for="field in section.data?.value.fields"
                    :key="field.propertyId"
                    v-model:form="form"
                    :field="field"
                    :lang="lang"
                    :prop="`properties.${field.propertyId}`"
                    :stats="props.stats"
                    :event="props.event"
                    :section="section"
                    :partners="form.partners"
                    :disabled="Boolean(props.guest?.extras?.roommates?.length && roommatePropertiesIds.includes(field.propertyId) && props.guest?.properties?.[field.propertyId])"
                    @update:property="(payload) => propertyUpdated({ propertyId: field.propertyId, value: payload })"
                  />

                  <PageFormBasicRoommates
                    v-if="section.data?.value?.roommatesMode === 'allowed' || section.data?.value?.roommatesMode === 'condition'"
                    :guest="props.guest"
                    :form="form"
                    :section="section"
                    :event="props.event"
                    :room-capacity="roomCapacity"
                    :roommate-properties-ids="roommatePropertiesIds"
                    :disabled="Boolean(props.guest?.extras?.roommates?.length)"
                  />
                </template>

                <div
                  v-if="partnersMaxLimit !== 0"
                  class="border-t border-gray-200 py-4"
                >
                  <div>
                    <InputBlock class="mb-0">
                      <Checkbox
                        :model-value="form.partners >= 1"
                        :label="section.$t('poly.iWillComeWithPlusOne')"
                        @update:model-value="(value) => {
                          form.partners = value ? 1 : 0;
                        }"
                      />
                    </InputBlock>

                    <InputBlock
                      v-if="form.partners >= 1 && partnersMaxLimit > 1"
                      :label="section.$t('poly.plusOneCount')"
                    >
                      <NumberInput
                        v-if="partnersMaxLimit > 1"
                        v-model="form.partners"
                        :min="1"
                        :max="partnersMaxLimit"
                      />
                    </InputBlock>
                  </div>
                </div>
              </template>


              <LazyPageFormBasicPayment
                v-if="isPaymentRequired"
                ref="paymentRef"
                :ticket-types="selectedTicketTypes"
                :form="form"
                :event="props.event"
                :lang="lang?.replace('_', '')"
                :section="section"
                :page-id="props.pageId"
                :form-section-id="currentInstance?.vnode?.key"
                :is-submitting="isSubmitting"
              />

              <LazyPageFormBasicBillingInfo
                v-if="isPaymentRequired"
                v-model:is-billing-info-shown="isBillingInfoShown"
                :form="form"
                :event="props.event"
                :lang="lang?.replace('_', '')"
                :section="section"
                :page-id="props.pageId"
              />

              <div
                v-if="section.data?.value?.consents?.length"
                class="border-t border-gray-200"
              >
                <div class="py-5">
                  <InputBlock
                    prop="givenConsents"
                    class="mb-0"
                  >
                    <div class="w-full">
                      <CheckboxGroup
                        :model-value="form.givenConsents || []"
                        :options="(section.data?.value?.consents || []).map((consent) => ({
                          id: consent[lang]?.text,
                          text: `${striptags(consent[lang]?.text || '', ['a', 'em', 'strong'])} <span class='text-red-500'>*</span>`,
                          description: getConsentDescription(consent),
                        }))"
                        @update:model-value="(value) => _set(form, 'givenConsents', value)"
                      />
                    </div>
                  </InputBlock>
                </div>
              </div>

              <Button
                type="primary"
                size="large"
                class="w-full"
                :loading="isPaymentRequired && isSubmitting"
                native-type="submit"
                :disabled="useRoute()?.path?.includes('/builder/') || !isMounted || isSubmitting"
              >
                <template v-if="isPaymentRequired">
                  {{ section.$t('poly.pay') }} {{ useFormatPrice(ticketsTotalPrice, props.event?.general?.ticketing?.currency) }}
                </template>

                <template v-else>
                  <template v-if="props.guest?.status?.confirmation > 0">
                    {{ section.$t('poly.updateRegistration') }}
                  </template>
                  <template v-else>
                    {{ props.data?.[lang]?.submitButtonText || (props.guest ? section.$t('poly.confirmAttendance') : section.$t('poly.doRegister', {}, i18nOptions)) }}
                  </template>
                </template>
              </Button>

              <div
                v-if="isPaymentRequired && props.event?.general?.ticketing?.taxRate?.percentage"
                class="font-normal text-gray-500 text-xs mt-4 text-center"
              >
                {{ section.$t('poly.tax.includes', {
                  percentage: props.event?.general?.ticketing?.taxRate?.percentage,
                  taxType: section.$t(`poly.tax.${props.event?.general?.ticketing?.taxRate?.type}`),
                }) }}
              </div>
            </Form>
          </transition>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import checkLottie from '~/assets/json/lottie/check.json';

const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
  guest: {
    type: Object,
    default: undefined,
  },
  event: {
    type: Object,
    default: undefined,
  },
  stats: {
    type: Object,
    default: () => ({}),
  },
  pageId: {
    type: String,
    default: undefined,
  },
  preview: {
    type: Boolean,
    default: false,
  },
  embedParams: {
    type: Object,
    default: null,
  },
});
const emit = defineEmits(['refresh-stats']);

// Refs
const section = usePageSection(props);
const lang = inject('lang');
const isMounted = useMounted();

const form = ref({
  properties: {},
  givenConsents: [],
  partners: 0,
});
const isSubmitting = ref(false);
const isSubmitted = ref(false);
const isBillingInfoShown = ref(false);
const currentInstance = getCurrentInstance();
const selectedTicketTypes = ref([]);
const isSelectingTicketTypes = ref(false);
const selectedTicketTypesBeforeEdit = ref([]);

// Template Refs
const paymentRef = useTemplateRef('paymentRef');

// Computed
const i18nOptions = computed(() => ({
  locale: lang.value.replace('_', ''),
}));

const baseProperties = computed(() => (ticketsTotalQuantity.value < 2
  ? [
    {
      id: 'firstName',
      [lang.value]: {
        name: section.$t('poly.firstName'),
      },
      type: 'text',
    },
    {
      id: 'lastName',
      [lang.value]: {
        name: section.$t('poly.lastName'),
      },
      type: 'text',
    },
    {
      id: 'email',
      [lang.value]: {
        name: section.$t('poly.email'),
      },
      type: 'text',
      canChange: props.event?.general?.ticketing?.isBulkPurchaseEnabled && props.guest?.extras?.payment?.buyerEmail && props.guest?.extras?.payment?.buyerEmail === props.guest?.properties?.email,
    },
  ]
  : [{ // bulk purchase
    id: 'email',
    [lang.value]: {
      name: section.$t('poly.email'),
    },
    type: 'text',
    canChange: props.event?.general?.ticketing?.isBulkPurchaseEnabled && props.guest?.extras?.payment?.buyerEmail && props.guest?.extras?.payment?.buyerEmail === props.guest?.properties?.email,
  }]));

const colorPalette = computed(() => useTokenColors(section.data?.value?.primaryColor).colorPalette?.value);
const rules = computed(() => {
  const fields = section.data?.value?.fields || [];
  const rules = {
    'properties.firstName': [
      { ...useFormRules().required, message: section.$t('poly.requiredField') },
    ],
    'properties.lastName': [
      { ...useFormRules().required, message: section.$t('poly.requiredField') },
    ],
    'properties.email': [
      { ...useFormRules().required, message: section.$t('poly.requiredField') },
    ],
  };

  if (!props.guest?.properties?.email) {
    rules['properties.email'].push({ ...useFormRules().email, message: section.$t('poly.emailNotValid') });
  }

  fields.forEach((field) => {
    if (field?.required) {
      const property = props.event?.guestlist?.properties?.find((property) => property.id === field.propertyId);
      if (property) {
        const hasAllOptionsCapacityReached = property.options?.every((option) => {
          const maxCapacity = field?.optionsSettings?.[option.id]?.maxCapacity;
          const stat = props.stats?.guestPropertyStats?.[`${field.propertyId}/${option.id}`];
          return Boolean(maxCapacity && stat >= maxCapacity);
        });

        if (!hasAllOptionsCapacityReached || property.type === 'text') {
          rules[`properties.${field.propertyId}`] = [
            { ...useFormRules().required, message: section.$t('poly.requiredField') },
          ];
        }
      }
    }
  });

  if ((section.data?.value?.roommatesMode === 'allowed' || section.data?.value?.roommatesMode === 'condition') && form.value?.extras?.roommates?.length) {
    for (let i = 0; i < (roomCapacity.value - 1); i++) {
      rules[`extras.roommates.${i}`] = [
        useFormRules().required,
      ];
    }
  }

  if (isTicketingEnabled.value && isBillingInfoShown.value) {
    rules['extras.billingInfo.address'] = [
      useFormRules().required,
    ];

    rules['extras.billingInfo.postcode'] = [
      useFormRules().required,
    ];

    rules['extras.billingInfo.city'] = [
      useFormRules().required,
    ];

    rules['extras.billingInfo.country'] = [
      useFormRules().required,
    ];
  }

  if (section.data?.value?.consents?.length > 0) {
    rules.givenConsents = [
      useFormRules().required,
      {
        validator: (rule, consents, callback) => {
          const requiredConsents = section.data?.value?.consents?.map((consent) => consent[lang.value]?.text);
          const hasAllRequiredConsents = requiredConsents.every((requiredConsent) => consents.includes(requiredConsent));

          if (hasAllRequiredConsents || useRoute()?.path?.includes('/builder/')) {
            callback();
          } else {
            callback(new Error(section.$t('poly.allConsentsRequired')));
          }
        },
      },
    ];
  }

  return rules;
});

const isRegistrationLimitValid = computed(() => {
  if (section.data.value?.registrationLimitType === 'none') {
    return true;
  }

  if (section.data.value?.registrationLimitType === 'date') {
    return useDayjs().isBefore(useDayjs(section.data.value?.registrationLimit));
  }
  if (section.data.value?.registrationLimitType === 'number') {
    return props.stats?.guestsConfirmed < section.data.value?.registrationLimit;
  }

  return true;
});
const partnersMaxLimit = computed(() => {
  if (section.data.value.partnersMode === 'allowed') {
    return section.data.value.partnersLimit;
  }

  if (section.data.value.partnersMode === 'condition') {
    const conditionLimits = (section.data.value.partnersLimit || [])
      .filter((condition) => condition && condition?.includes('/'))
      .map((condition) => {
        const [propertyId, optionId, limit] = condition.split('/');
        const propertyValue = form.value?.properties?.[propertyId];
        const eventProperty = props.event?.guestlist?.properties?.find((property) => property.id === propertyId);
        const eventOption = eventProperty?.options?.find((option) => option.id === propertyValue);

        if (eventProperty && eventOption) {
          const hasOption = Array.isArray(propertyValue) ? propertyValue.includes(optionId) : propertyValue === optionId;

          return hasOption ? Number(limit) : 0;
        } else {
          return 0;
        }
      });

    return conditionLimits.length ? Math.max(...conditionLimits) : 0;
  }

  return 0;
});
const isBeforeEndDate = computed(() => {
  if (props.preview) {
    return true;
  }

  if (!props.event?.general?.endDate) {
    return true;
  }

  return useDayjs().isBefore(useDayjs(props.event?.general?.endDate));
});
const roomCapacity = computed(() => {
  if (section.data.value.roommatesMode === 'allowed') {
    return section.data.value.roommatesRoomSize;
  }

  if (section.data.value.roommatesMode === 'condition') {
    const capacitiesParsed = (section.data.value.roommatesRoomSize || [])
      .map((capacityString) => {
        const [propertyId, optionId, capacity] = capacityString.split('/');

        return {
          propertyId,
          optionId,
          capacity: capacity ? Number(capacity) : 0,
        };
      });

    const foundCapacity = capacitiesParsed.find(({ propertyId, optionId }) => {
      const propertyValue = form.value?.properties?.[propertyId];
      const eventProperty = props.event?.guestlist?.properties?.find((property) => property.id === propertyId);
      const eventOption = eventProperty?.options?.find((option) => option.id === propertyValue);

      if (eventProperty && eventOption) {
        return Array.isArray(propertyValue) ? propertyValue.includes(optionId) : propertyValue === optionId;
      }
    });

    return foundCapacity?.capacity || 0;
  }

  return 0;
});
const roommatePropertiesIds = computed(() => {
  if (section.data?.value?.roommatesMode === 'condition') {
    return section.data?.value?.roommatesRoomSize?.map((capacity) => capacity.split('/')?.[0]);
  }

  return [];
});

const isTicketingEnabled = computed(() => props.event?.general?.ticketing?.isEnabled && useWorkspaceStore().integrations?.stripe?.isEnabled);
const ticketingTypesFiltered = computed(() => props.event?.general?.ticketing?.types?.filter((ticketingType) => !ticketingType.hasRestrictedVisibility || isMatchPropertyOptionConditions(ticketingType.visibleOnlyTo, form.value?.properties)));
const isPaymentRequired = computed(() => isTicketingEnabled.value && ticketsTotalPrice.value > 0 && !props.guest?.extras?.payment?.isPaid);

const selectedTicketTypesPopulated = computed(() => (selectedTicketTypes.value || []).map(({ id, quantity }) => ({ ..._find(props.event?.general?.ticketing?.types, { id }), quantity })));
const ticketsTotalQuantity = computed(() => (selectedTicketTypesPopulated.value || []).reduce((acc, ticketType) => acc + ticketType.quantity, 0));
const ticketsTotalPromoCodesDiscount = computed(() => (form.value?.extras?.payment?.usedPromoCodes || [])?.reduce((acc, promoCode) => acc + promoCode.amount, 0));
const ticketsTotalPrice = computed(() => Math.max(0, (selectedTicketTypesPopulated.value || []).reduce((acc, ticketType) => acc + ticketType.price * ticketType.quantity, 0) - ticketsTotalPromoCodesDiscount.value));

// Methods
const getConsentDescription = (consent) => {
  const attachedFile = consent[lang.value]?.attachedFile?.link;
  if (attachedFile) {
    return `<a href="${attachedFile}" target="_blank">${section.$t('poly.learnMore')}</a>`;
  }

  return undefined;
};

const onSubmit = async() => {
  if (isSubmitting.value) {
    return;
  }

  isSubmitting.value = true;

  let paymentIntent;
  if (!props.guest?.extras?.payment?.isPaid && ticketsTotalPrice.value > 0) {
    paymentIntent = await paymentRef.value?.pay().catch((error) => {
      console.error('[PAY]', error);
      return;
    });

    if (!paymentIntent || paymentIntent.status !== 'succeeded') {
      isSubmitting.value = false;

      useToast.error(section.$t('poly.paymentFailed'));
      return;
    }
  }

  const formData = form.value;

  const response = await useApi.post('/public/guests/register', {
    body: {
      formData,
      eventId: props.event?._id,
      formSectionId: currentInstance?.vnode?.key,
      guestId: props.guest?._id,
      pageId: props.pageId,
      lang: lang.value?.replace('_', ''),
      paymentIntent: paymentIntent?.id,
      ticketTypes: selectedTicketTypes.value,
    },
  }).catch((error) => {
    if (error?.statusCode === 400 && error?.response?._data) {
      useToast.error(error.response._data);
      emit('refresh-stats');
    } else {
      useToast.error(section.$t('poly.errorLoadingDataRefresh'));
    }
    return false;
  });
  isSubmitting.value = false;

  if (response) {
    isSubmitted.value = true;
  }
};

const propertyUpdated = ({ propertyId }) => {
  if ((roommatePropertiesIds.value || []).includes(propertyId) && form.value.extras?.roommates?.length) {
    form.value.extras.roommates = undefined;
  }
};

const hasDifferentTicketTypes = (selectedTicketTypesBeforeEdit, selectedTicketTypes) => {
  const unifyTicketTypes = (ticketTypes) => ticketTypes.map((ticketType) => `${ticketType.quantity}x-${ticketType.id}`).join(',');
  return unifyTicketTypes(selectedTicketTypesBeforeEdit) !== unifyTicketTypes(selectedTicketTypes);
};

const resetForm = () => {
  if (props.guest) {
    form.value = {
      ..._cloneDeep(props.guest),
      properties: {
        ...props.guest?.properties || {},
      },
      partners: 0,
      givenConsents: props.guest?.status?.givenConsents || [],
    };

    if (props.guest?.status?.confirmation <= 0) {
      form.value = {
        ...form.value,
        partners: 0,
      };
    }

    if (props.guest?.status?.confirmation > 0) {
      form.value = {
        ...form.value,
        partners: props.guest?.status?.confirmation - 1,
      };
    }

    if (props.event?.general?.ticketing?.isBulkPurchaseEnabled && props.guest?.extras?.payment?.buyerEmail && props.guest?.extras?.payment?.buyerEmail === props.guest?.properties?.email) {
      form.value.properties.email = '';
    }

    if (props.event?.general?.ticketing?.hasPromoCodesEnabled) {
      _set(form.value, 'extras.payment.usedPromoCodes', undefined);
    }
  } else {
    form.value = {
      ...form.value,
      properties: {
        firstName: '',
        lastName: '',
        email: '',
        lang: section.lang?.value?.replace('_', ''),
      },
      givenConsents: [],
      partners: 0,
      extras: form.value.extras ?
        {
          ...form.value.extras,
          payment: Object.keys(form.value.extras?.payment || {}).length
            ? {
              ...form.value.extras?.payment,
              usedPromoCodes: undefined,
            }
            : undefined,
        }
        : undefined,
    };
  }
};

// Watchers
watch(() => props.guest, () => {
  resetForm();

  if (isTicketingEnabled.value) {
    if (props.guest?.properties?.ticketType) { // if guest has a ticket type, can't change it
      selectedTicketTypes.value = [{
        id: props.guest?.properties?.ticketType,
        quantity: 1,
      }];
      isSelectingTicketTypes.value = false;
    } else {
      isSelectingTicketTypes.value = true;
      selectedTicketTypesBeforeEdit.value = [];

      // preselect the first available ticketing type
      const firstAvailableTicketingType = ticketingTypesFiltered.value.find((ticketingType) => ticketingType.availableCapacity !== 0);
      if (firstAvailableTicketingType && !form.value.properties.ticketType) {
        selectedTicketTypes.value = [{
          id: firstAvailableTicketingType.id,
          quantity: 1,
        }];

        if (!props.event?.general?.ticketing?.isBulkPurchaseEnabled && ticketingTypesFiltered.value?.length === 1) { // if there is only one ticketing type, continue to the next step automatically
          form.value.properties.ticketType = selectedTicketTypes.value[0]?.id;
          isSelectingTicketTypes.value = false;
        }
      }
    }
  }
}, { immediate: true });
</script>

<style>
.lottie-check {
  path[fill="rgb(40,166,69)"] {
    fill: var(--section-primary-color);
  }

  path[fill="rgb(239,255,243)"] {
    fill: var(--section-primary-color);
    fill-opacity: 0.15;
  }
}
</style>
